import {
  changeCount,
  changeSelectedRows,
  setRoute,
  resetTransmissionState,
} from "app/store/transmission/slice/transmissionSlice";
import { useSelector, useDispatch } from "react-redux";
import {
  CLINIC_FOR_REVIEW,
  CLINIC_ROLES,
  TRANSMISSION_TAB_COUNT_ACCESS,
} from "static/constants";
import {
  transmissionTableApi,
  useGetReportsCountQuery,
} from "app/store/transmission/api/transmissionTableApi";
import {
  resolveFollowingHospitals,
  resolveFollowingPhysicians,
  resolveMyPatientsFilter,
} from "utils/transmissionTable.query";
import { useEffect, useMemo } from "react";

const useTransmissionTab = (role, tabStyles) => {
  const dispatch = useDispatch();

  const clinicReviewer = localStorage.getItem("clinicReviewer") === "true";
  const { selectedTab, transmissionCount, isMyPatientsFilter } = useSelector(
    (state) => state.transmission
  );
  const { currentUser } = useSelector((state) => state.auth);
  const { accountsMapper } = useSelector((state) => state.account);
  const clinicName = localStorage.getItem("clinicName");

  const physicians = useMemo(
    () =>
      resolveFollowingPhysicians(
        localStorage.getItem("followingPhysicians")
      ).join(","),
    []
  );

  const hospitals = resolveFollowingHospitals(
    currentUser?.followingHospitals,
    currentUser?.isFollowingHospital
  );

  const myPatientFilter =
    isMyPatientsFilter ??
    resolveMyPatientsFilter({
      view: CLINIC_FOR_REVIEW,
      showOnlyAssignedReports:
        accountsMapper?.[clinicName]?.settings?.show_only_assigned_reports,
      myPatientsPreference: currentUser?.user_prefs?.my_patients_toggled,
      storedMyPatientsToggled: localStorage.getItem(
        `myPatientsToggled:${currentUser?.userId}`
      ),
    });

  const { data: counts, fulfilledTimeStamp } = useGetReportsCountQuery(
    { physicians, hospitals, isMyPatientsFilter: myPatientFilter },
    { skip: !selectedTab, refetchOnMountOrArgChange: true }
  );

  const applyCounts = (openTab, openCount) =>
    Object.entries(counts ?? {})
      .filter(
        ([key]) =>
          key !== openTab ||
          openCount === undefined ||
          !TRANSMISSION_TAB_COUNT_ACCESS.includes(key)
      )
      .forEach(([key, value]) =>
        dispatch(changeCount({ view: key, count: value }))
      );

  useEffect(() => {
    applyCounts(selectedTab, transmissionCount?.[selectedTab]);
  }, [counts, fulfilledTimeStamp]);

  const tabs = [
    {
      label: "Active",
      scope: ["admin", "assistant", "user"],
      value: "active",
      totalCount: transmissionCount?.active,
      style: tabStyles("active", role !== "clinic"),
    },
    {
      label: "Processed",
      scope: ["admin", "assistant", "user"],
      value: "processed",
      style: tabStyles("processed", role !== "clinic"),
    },
    {
      label: "Active",
      scope: CLINIC_ROLES,
      value: "clinic",
      totalCount: transmissionCount?.clinic,
      style: tabStyles("clinic", role === "clinic"),
    },
    {
      label: "Review",
      scope: CLINIC_ROLES,
      value: "clinic-review",
      totalCount: transmissionCount["clinic-review"],
      style: tabStyles("clinic-review", role === "clinic" && !clinicReviewer),
    },
    {
      label: "Signed/Archived",
      scope: CLINIC_ROLES,
      value: "clinic-processed",
      style: tabStyles("clinic-processed", role === "clinic"),
    },
  ];

  const onTabChange = (value) => {
    const shownCounts = transmissionCount;

    dispatch(changeSelectedRows({}));
    dispatch(resetTransmissionState());
    dispatch(setRoute(value));
    Object.entries(shownCounts).forEach(([view, count]) =>
      dispatch(changeCount({ view, count }))
    );
    dispatch(transmissionTableApi.util.invalidateTags(["transmissionsCount"]));
  };

  return { tabs, selectedTab, onTabChange };
};

export default useTransmissionTab;
